import React, { useState } from "react";
import clsx from "clsx";
import { X } from "lucide-react";

// Good enough to catch a typo before the server's stricter check: something@something.tld
export const looksLikeEmail = (value) =>
  /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test((value ?? "").trim());

export const DEFAULT_MAX_RECIPIENTS = 10;

/**
 * Splits whatever was typed or pasted into addresses: commas, semicolons and whitespace all
 * separate, so "a@x.com, b@x.com; c@x.com" from an Outlook To: line pastes in one go.
 */
export const splitAddresses = (text) =>
  (text ?? "")
    .split(/[\s,;]+/)
    .map((part) => part.trim())
    .filter(Boolean);

/**
 * A list of email addresses as chips, with a box to add more.
 *
 * Enter, comma, semicolon, Tab or leaving the box adds what was typed; Backspace on an empty box
 * takes the last chip back into the box to edit. Clicking a chip's pencil edits it in place.
 * Duplicates (case-insensitive) and the owner's own address are refused with a message rather
 * than silently dropped, so the user knows why the chip did not appear.
 */
const EmailRecipientsInput = ({
  value = [],
  onChange,
  ownerEmail = null,
  max = DEFAULT_MAX_RECIPIENTS,
  disabled = false,
  id = "email-recipients",
}) => {
  const [draft, setDraft] = useState("");
  const [error, setError] = useState(null);

  const commit = (text) => {
    const candidates = splitAddresses(text);
    if (!candidates.length) {
      setDraft("");
      return true;
    }

    // Whatever fits is added even when a later address is refused: a pasted list of eight
    // with one typo should leave seven chips and the typo in the box, not nothing.
    const next = [...value];
    let problem = null;
    let leftover = "";
    for (const candidate of candidates) {
      if (!looksLikeEmail(candidate)) {
        problem = `“${candidate}” doesn't look like an email address.`;
        leftover = candidate;
        break;
      }
      if (ownerEmail && candidate.toLowerCase() === ownerEmail.toLowerCase()) {
        problem = "That's your own address — use “Send to my email” above.";
        leftover = candidate;
        break;
      }
      if (next.some((existing) => existing.toLowerCase() === candidate.toLowerCase())) {
        problem = `${candidate} is already on the list.`;
        break;
      }
      if (next.length >= max) {
        problem = `You can add up to ${max} addresses.`;
        leftover = candidate;
        break;
      }
      next.push(candidate);
    }

    if (next.length !== value.length) onChange(next);
    setError(problem);
    setDraft(leftover);
    return problem === null;
  };

  const remove = (index) => {
    setError(null);
    onChange(value.filter((_, i) => i !== index));
  };

  const edit = (index) => {
    if (draft.trim() && !commit(draft)) return;
    const address = value[index];
    onChange(value.filter((_, i) => i !== index));
    setDraft(address);
    setError(null);
  };

  const onKeyDown = (event) => {
    if (disabled) return;
    if (["Enter", ",", ";", "Tab"].includes(event.key)) {
      if (!draft.trim() && event.key === "Tab") return;
      event.preventDefault();
      commit(draft);
    } else if (event.key === "Backspace" && !draft && value.length) {
      event.preventDefault();
      edit(value.length - 1);
    }
  };

  const full = value.length >= max;

  return (
    <div className="flex flex-col gap-1.5">
      <div
        className={clsx(
          "flex flex-wrap items-center gap-1.5 rounded-lg border px-2.5 py-2 min-h-[44px] bg-white",
          error ? "border-red-100" : "border-dimgray-400",
          disabled && "bg-whitesmoke-100 opacity-70"
        )}
        onClick={() => document.getElementById(id)?.focus()}
      >
        {value.map((address, index) => (
          <span
            key={address}
            className="inline-flex items-center gap-1 rounded-full bg-blue-200 text-dark-blue-100 text-[13px] pl-3 pr-1 py-1 max-w-full"
          >
            <button
              type="button"
              className="truncate max-w-[240px] hover:underline disabled:no-underline"
              title="Edit this address"
              onClick={(e) => {
                e.stopPropagation();
                if (!disabled) edit(index);
              }}
              disabled={disabled}
            >
              {address}
            </button>
            <button
              type="button"
              aria-label={`Remove ${address}`}
              className="rounded-full p-0.5 text-dimgray-200 hover:bg-white hover:text-red-100 disabled:hover:bg-transparent"
              onClick={(e) => {
                e.stopPropagation();
                if (!disabled) remove(index);
              }}
              disabled={disabled}
            >
              <X size={14} aria-hidden="true" />
            </button>
          </span>
        ))}
        <input
          id={id}
          type="text"
          inputMode="email"
          autoComplete="off"
          aria-label="Add an email address"
          aria-invalid={!!error}
          aria-describedby={error ? `${id}-error` : undefined}
          className="flex-1 min-w-[180px] bg-transparent text-[14px] text-dark-blue-100 placeholder:text-dimgray-400 focus:outline-none disabled:cursor-not-allowed"
          placeholder={
            full ? `Up to ${max} addresses` : value.length ? "Add another address" : "name@company.com"
          }
          value={draft}
          disabled={disabled || full}
          onChange={(e) => {
            const text = e.target.value;
            // A pasted list arrives here in one go; a separator inside it means "add these".
            if (/[,;]|\s\S/.test(text.trim())) {
              commit(text);
              return;
            }
            setDraft(text);
            if (error) setError(null);
          }}
          onKeyDown={onKeyDown}
          onBlur={() => {
            if (draft.trim()) commit(draft);
          }}
        />
      </div>
      {error ? (
        <p id={`${id}-error`} role="alert" className="text-[12px] text-red-100">
          {error}
        </p>
      ) : (
        <p className="text-[12px] text-dimgray-200">
          Press Enter after each address, or paste a list separated by commas.
        </p>
      )}
    </div>
  );
};

export default EmailRecipientsInput;
